import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { Field, Chips } from './RegistryField';
import { CHANNEL_LABELS, rendererForChannels, unusedSettingGroupIdForTitle } from '../../../helpers/notification_registry_helpers';

// What creators will see on their Settings page for a switch that does not exist yet. Opened from
// New notification and from an existing notification's plan, so a switch can be proposed at either
// point without the two forms drifting.

const NEW_GROUP = '__new_group__';

// What each renderer means on the Settings page, so the channels chosen read as a consequence.
const RENDERER_NOTES = {
  normal: 'one row with a push toggle and an email toggle',
  pushOnly: 'drawn as push alone; the lane\u2019s email still sends',
  webOnly: 'drawn as email alone, with no push toggle'
};

// What the server would refuse, said before the save rather than after it.
export const switchProposalGaps = proposal => {
  if (!proposal) return ['a switch'];
  return [
    !proposal.display?.trim() && 'a label',
    !proposal.setting?.trim() && 'a column',
    !proposal.channels?.length && 'at least one channel',
    proposal.newGroup && !proposal.newGroup.title?.trim() && 'a name for the new group',
    !proposal.newGroup && !proposal.groupId && 'a settings group'
  ].filter(Boolean);
};

export const emptySwitchDefinition = groups => ({
  display: '',
  subdisplay: '',
  groupId: groups[0]?.id || '',
  channels: ['push', 'email'],
  isRecommended: false,
  defaultOn: true,
  setting: ''
});

const SwitchBuilder = ({ groups, definition, onChange, settled = false, onEdit = null }) => {
  const current = definition || emptySwitchDefinition(groups);
  const isNewGroup = !!current.newGroup;
  const update = changes => {
    const next = { ...current, ...changes };
    if (changes.display !== undefined && !current.settingEdited) next.setting = `notifyOn${_.upperFirst(_.camelCase(changes.display))}`;
    onChange(next);
  };
  // A new group has to be placed against one that already ships, since the Settings page is an order.
  const startNewGroup = () =>
    onChange(_.omit({ ...current, newGroup: { id: '', title: '', insertAfterGroupId: _.last(groups)?.id || '' } }, 'groupId'));
  const useExistingGroup = () => onChange(_.omit({ ...current, groupId: groups[0]?.id || '' }, 'newGroup'));
  const updateNewGroup = changes => {
    const newGroup = { ...current.newGroup, ...changes };
    if (changes.title !== undefined) newGroup.id = unusedSettingGroupIdForTitle(changes.title);
    onChange({ ...current, newGroup });
  };
  const closest = closestSwitch(groups, current.display);
  return (
    <div className={settled ? 'switch-builder settled' : 'switch-builder'}>
      <div className='card-head'>
        <span className='card-title'>New switch</span>
        <span className='card-note'>what creators will see on their Settings page</span>
        {onEdit && (
          <button type='button' className='edit-proposal' onClick={onEdit} aria-label='Edit proposal'>
            ✎
          </button>
        )}
      </div>
      <div className='new-form inner'>
        <Field label='Label creators see' hint='e.g. Payouts'>
          <input value={current.display} onChange={e => update({ display: e.target.value })} />
        </Field>
        <Field label='Settings group' hint={isNewGroup ? 'e.g. Earnings & Payments' : undefined}>
          {isNewGroup ? (
            <>
              <input autoFocus value={current.newGroup.title} onChange={e => updateNewGroup({ title: e.target.value })} />
              <button type='button' className='quiet' onClick={useExistingGroup}>
                Use a group that already exists
              </button>
            </>
          ) : (
            <select
              value={current.groupId || ''}
              onChange={e => (e.target.value === NEW_GROUP ? startNewGroup() : update({ groupId: e.target.value }))}
            >
              {groups.map(group => (
                <option key={group.id} value={group.id}>
                  {group.title}
                </option>
              ))}
              <option value={NEW_GROUP}>+ New group…</option>
            </select>
          )}
        </Field>
        {isNewGroup && (
          <Field label='Where it sits' hint='the Settings page is an order, so a new group goes after one that already exists'>
            <select value={current.newGroup.insertAfterGroupId} onChange={e => updateNewGroup({ insertAfterGroupId: e.target.value })}>
              {groups.map(group => (
                <option key={group.id} value={group.id}>
                  After {group.title}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label='Description under the label' hint='e.g. Get notified when a payout is on its way to you.' full>
          <input value={current.subdisplay} onChange={e => update({ subdisplay: e.target.value })} />
        </Field>
        <Field label='Channels' hint={RENDERER_NOTES[rendererForChannels(current.channels)]}>
          <Chips
            values={['push', 'email']}
            selected={current.channels}
            labels={CHANNEL_LABELS}
            onToggle={value =>
              update({ channels: current.channels.includes(value) ? current.channels.filter(c => c !== value) : [...current.channels, value] })
            }
          />
        </Field>
        <Field label='Recommended'>
          <select value={current.isRecommended ? 'yes' : 'no'} onChange={e => update({ isRecommended: e.target.value === 'yes' })}>
            <option value='yes'>Yes, warn when turned off</option>
            <option value='no'>No</option>
          </select>
        </Field>
        <Field label='Column' hint='from the label; editable'>
          <input className='mono' value={current.setting} onChange={e => onChange({ ...current, setting: e.target.value, settingEdited: true })} />
        </Field>
        <Field label='Default'>
          <select value={current.defaultOn ? 'on' : 'off'} onChange={e => update({ defaultOn: e.target.value === 'on' })}>
            <option value='on'>On for everyone</option>
            <option value='off'>Off until she turns it on</option>
          </select>
        </Field>
      </div>
      {closest && (
        <div className='builder-note'>
          <span className='badge neutral'>Closest existing</span>
          <span>
            <b>{closest.label}</b> (<code>{closest.setting}</code>) already covers {closest.description.toLowerCase().replace(/\.$/, '')}. If this
            belongs there, choose it instead; one switch for related notifications is kinder than many.
          </span>
        </div>
      )}
      <div className='builder-note'>
        <span className='badge warn'>Not in code yet</span>
        <span>
          Saved with the notification. The template starter adds the column and this Settings row; creators see it on web and app once the PR ships,
          and the Gate waits until then.
        </span>
      </div>
    </div>
  );
};

SwitchBuilder.propTypes = {
  groups: PropTypes.array.isRequired,
  definition: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  settled: PropTypes.bool,
  onEdit: PropTypes.func
};

// A crude nearness: the existing switch sharing the most words with the typed label.
const closestSwitch = (groups, label) => {
  const words = _.words(label.toLowerCase()).filter(word => word.length > 3);
  if (!words.length) return null;
  const options = _.flatMap(groups, group => group.options);
  const scored = options.map(option => ({
    option,
    score: words.filter(word => `${option.label} ${option.description}`.toLowerCase().includes(word)).length
  }));
  const best = _.maxBy(scored, 'score');
  return best?.score ? best.option : null;
};

export default SwitchBuilder;
